import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { ComboBox, InlineLoading } from '@carbon/react';
import { WarningFilled } from '@carbon/react/icons';
import { useAtom, useSetAtom } from 'jotai';
import { useStore } from '@tanstack/react-store';
import { debounce } from 'lodash-es';

import { FreeTextComboBox } from 'components/free-text-combobox';

import { AfeWizardCtx } from '../context';
import {
  afeWellIntersectionAtom,
  wellQueryAtom,
  basinsLoadableAtom,
  formationsLoadableAtom,
  uwiTypeaheadLoadableAtom,
} from '../atoms';
import type { BasinOption, FormationOption, TypeaheadResult } from '../types';
import { DnCSection } from './dnc-section';
import styles from './well-information-step.module.scss';

export interface WellFormProps {
  onBasinChange: (basinName: string | null) => void;
  showDcAndE: boolean;
}

/**
 * Form fields for Existing Well type. Renders UWI server typeahead,
 * Basin and Formation client-filtered ComboBoxes, and conditional D&C section.
 * @param {WellFormProps} props - Basin change handler and D&C visibility flag
 * @return {JSX.Element} The existing well form fields
 */
const ExistingWellForm: FC<WellFormProps> = ({ onBasinChange, showDcAndE }) => {
  const { form } = useContext(AfeWizardCtx)!;
  const [uwiState, searchUwi] = useAtom(uwiTypeaheadLoadableAtom);
  const [basinsLoadable, triggerBasins] = useAtom(basinsLoadableAtom);
  const triggerWellMap = useSetAtom(wellQueryAtom);
  const triggerIntersection = useSetAtom(afeWellIntersectionAtom);
  const [formationsLoadable, triggerFormations] = useAtom(
    formationsLoadableAtom
  );
  const debouncedSearchUwi = useMemo(
    () => debounce(searchUwi, 400),
    [searchUwi]
  );

  useEffect(() => {
    return () => debouncedSearchUwi.cancel();
  }, [debouncedSearchUwi]);

  const uwiResults: TypeaheadResult[] =
    uwiState.state === 'hasData' && uwiState.data ? uwiState.data.results : [];
  const uwiLoading = uwiState.state === 'loading';
  const uwiError = uwiState.state === 'hasError';

  const basins: BasinOption[] =
    basinsLoadable.state === 'hasData' && basinsLoadable.data
      ? basinsLoadable.data
      : [];
  const loadingBasins = basinsLoadable.state === 'loading';
  const basinsError = basinsLoadable.state === 'hasError';

  const formations: FormationOption[] =
    formationsLoadable.state === 'hasData' && formationsLoadable.data
      ? formationsLoadable.data
      : [];
  const loadingFormations = formationsLoadable.state === 'loading';
  const formationsError = formationsLoadable.state === 'hasError';

  const uwi = useStore(form.store, (s) => s.values.uwi);
  const basinName = useStore(form.store, (s) => s.values.basinName);
  const reportedFormation = useStore(
    form.store,
    (s) => s.values.reportedFormation
  );

  const [formationsBasin, setFormationsBasin] = useState<
    string | null | undefined
  >(undefined);

  const selectedBasin = basinName
    ? (basins.find((b) => b.value === basinName) ?? {
        value: basinName,
        label: basinName,
      })
    : null;
  const selectedFormation = reportedFormation
    ? (formations.find((f) => f.value === reportedFormation) ?? {
        value: reportedFormation,
        label: reportedFormation,
      })
    : null;

  // Seed items with current form value when no search results (e.g. remount)
  const uwiItems: TypeaheadResult[] =
    uwiResults.length > 0 ? uwiResults : uwi ? [{ id: 0, value: uwi }] : [];
  const selectedUwi = uwi
    ? (uwiItems.find((i) => i.value === uwi) ?? null)
    : null;

  const populateWellFields = async (uwiValue: string) => {
    const data = await triggerWellMap(uwiValue);
    const well = data?.results[0];
    if (well) {
      form.setFieldValue('wellName', well.name || null);
      form.setFieldValue('wellOperator', well.currentOperator || null);
      form.setFieldValue('trueVerticalDepth', well.trueVerticalDepthFeet);
    }
  };

  const populateWellUnit = async (uwiValue: string) => {
    const data = await triggerIntersection(uwiValue);
    const assignedUnit = data?.intersectingUnits.find((u) => u.assignedToUnit);
    form.setFieldValue('wellUnit', assignedUnit?.name ?? null);
  };

  return (
    <div className={styles.formSection}>
      <div className={styles.typeaheadWrapper}>
        <ComboBox
          id="uwi"
          titleText="UWI *"
          items={uwiItems}
          selectedItem={selectedUwi}
          itemToString={(item: TypeaheadResult | null) => item?.value ?? ''}
          onInputChange={(text: string) => {
            if (text && text !== form.state.values.uwi)
              debouncedSearchUwi(text);
          }}
          onChange={({ selectedItem }: any) => {
            debouncedSearchUwi.cancel();
            const uwiValue = selectedItem?.value ?? null;
            form.setFieldValue('uwi', uwiValue);
            form.setFieldValue('slotId', uwiValue);
            if (uwiValue) {
              populateWellFields(uwiValue);
              populateWellUnit(uwiValue);
            }
          }}
          shouldFilterItem={() => true}
          placeholder="Search by UWI..."
          className={styles.field}
        />
        {uwiLoading && (
          <InlineLoading description="" className={styles.spinner} />
        )}
        {uwiError && <WarningFilled className={styles.errorIcon} />}
      </div>
      {showDcAndE && <DnCSection />}
      <div
        className={styles.typeaheadWrapper}
        onFocus={() => {
          if (basins.length === 0 && !loadingBasins) triggerBasins();
        }}
      >
        <FreeTextComboBox
          id="basin"
          key={`basin-${basinName ?? 'none'}`}
          titleText="Basin"
          items={basins}
          itemToString={(item) => (item as BasinOption | null)?.label ?? ''}
          selectedItem={selectedBasin}
          onChange={({ selectedItem }) => {
            const value = (selectedItem as BasinOption | null)?.value ?? null;
            if (value !== basinName) {
              onBasinChange(value);
            }
          }}
          placeholder="Select a basin"
          className={styles.field}
        />
        {loadingBasins && (
          <InlineLoading description="" className={styles.spinner} />
        )}
        {basinsError && <WarningFilled className={styles.errorIcon} />}
      </div>
      <div
        className={styles.typeaheadWrapper}
        onFocus={() => {
          if (!loadingFormations && formationsBasin !== basinName) {
            setFormationsBasin(basinName);
            triggerFormations(basinName);
          }
        }}
      >
        <FreeTextComboBox
          id="formation"
          key={`formation-${basinName ?? 'none'}`}
          titleText="Reported Formation"
          items={formations}
          itemToString={(item) => (item as FormationOption | null)?.label ?? ''}
          selectedItem={selectedFormation}
          onChange={({ selectedItem }) => {
            const value =
              (selectedItem as FormationOption | null)?.value ?? null;
            if (value !== reportedFormation) {
              form.setFieldValue('reportedFormation', value);
            }
          }}
          placeholder="Select a formation"
          className={styles.field}
        />
        {loadingFormations && (
          <InlineLoading description="" className={styles.spinner} />
        )}
        {formationsError && <WarningFilled className={styles.errorIcon} />}
      </div>
    </div>
  );
};

export { ExistingWellForm };
